import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import FormField from "../../components/account/FormField";
import SpinnerIcon from "../../components/global/SpinnerIcon";
import { updateContact } from "../../store/actions/UserActions";
import { US_STATES } from "../../utils/usStates";

/*
    MY DETAILS — the fields that used to live in the "Update Contact
    Information" modal, now a page.

    Two things changed on the way out of the modal:

      * State is a dropdown. It was a free-text box here while checkout has
        used the US_STATES react-select since #26, so the same customer could
        store "Minnesota" on one form and "MN" on the other. Same list, same
        control, one answer.

      * The form reads the signed-in user from Redux rather than re-parsing
        localStorage["authUser"] the way the modal did. updateContact
        dispatches LOGIN on success, so the store is what is up to date after
        a save; localStorage was a second copy that only agreed by luck.
*/

// Everything here is required except Web Address — stated once, as data, so
// the field list stays the thing you edit to change the form.
const FIELDS = [
  { name: "email", label: "Email", type: "email", autoComplete: "email" },
  { name: "fullname", label: "Full Name", autoComplete: "name" },
  { name: "address", label: "Address", autoComplete: "street-address" },
  { name: "city", label: "City", autoComplete: "address-level2" },
  {
    name: "state",
    label: "State",
    options: US_STATES,
    placeholder: "Select a state",
  },
  { name: "zip_code", label: "Zip Code", autoComplete: "postal-code" },
  { name: "date_of_birth", label: "Date of Birth", type: "date" },
  { name: "company", label: "Company", autoComplete: "organization" },
  {
    name: "webaddress",
    label: "Web Address",
    type: "url",
    optional: true,
    placeholder: "https://example.com",
  },
];

const MyDetails = () => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.authUser);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    email: user?.email || "",
    fullname: user?.fullname || "",
    address: user?.address || "",
    city: user?.city || "",
    state: user?.state || "",
    zip_code: user?.zip_code || "",
    // The API sends a full ISO timestamp; <input type="date"> only accepts
    // YYYY-MM-DD and silently renders blank for anything else.
    date_of_birth: user?.date_of_birth?.substring(0, 10) || "",
    // "Self" is the default for someone who is not buying on behalf of a
    // company — applied only when the field is genuinely empty, so it never
    // overwrites a company a customer has already given us.
    company: user?.company || "Self",
    webaddress: user?.webaddress || "",
  });

  const handleInputChange = ({ target: { name, value } }) =>
    setFormData((prev) => ({ ...prev, [name]: value }));

  const handleSubmit = (e) => {
    e.preventDefault();

    const missing = FIELDS.filter(
      (field) => !field.optional && !String(formData[field.name] || "").trim()
    );
    if (missing.length > 0) {
      toast.error(`Please fill in ${missing[0].label}.`);
      return;
    }

    setSaving(true);
    // `finally`, not the success callback: updateContact swallows its own
    // errors and only calls back when the save worked, so a failed request
    // would otherwise leave the button spinning for good.
    dispatch(
      updateContact(formData, () =>
        toast.success("Your details have been updated")
      )
    ).finally(() => setSaving(false));
  };

  return (
    <>
      <h1 className="account__heading">My Details</h1>

      <div className="account__panel">
        <form className="account__form" onSubmit={handleSubmit} noValidate>
          {FIELDS.map((field) => (
            <FormField
              key={field.name}
              {...field}
              value={formData[field.name]}
              onChange={handleInputChange}
              required={!field.optional}
              disabled={saving}
            />
          ))}

          <button
            type="submit"
            className="gb-btn gb-btn--primary"
            disabled={saving}
          >
            {saving ? (
              <>
                <SpinnerIcon /> Updating
              </>
            ) : (
              "Update"
            )}
          </button>
        </form>
      </div>
    </>
  );
};

export default MyDetails;
